import { Colors, Metrics } from "../../resources";
import { Pressable, StyleSheet, View, ViewStyle } from "react-native";
import React, { ComponentType, useRef, useState } from "react";

import SIcon from "../atoms/SIcon";
import { SIconType } from "../../types";
import { ifElse } from "../../utils";
import { noop } from "lodash";
import { useOnclickOutside } from "../../hooks";

// react-native-web/overrides.ts
declare module "react-native" {
  interface PressableProps
    extends AccessibilityProps,
      Omit<ViewProps, "style" | "hitSlop"> {
    /**
     * Called when a single tap gesture is detected.
     */
    onMouseEnter?: null | ((event: GestureResponderEvent) => void);
    onMouseLeave?: null | ((event: GestureResponderEvent) => void);
  }
}

type ExtraInfoType = {
  onCancel: () => void;
};

interface IProps {
  type: SIconType;
  size?: number;
  isContainer?: boolean;
  disabled?: boolean;
  containerStyle?: ViewStyle;
  children?: any;
  onPress?: () => void;
}

const SIconButton: React.FC<IProps> = ({
  type,
  size = Metrics.iconLarge,
  isContainer = false,
  disabled = false,
  containerStyle = {},
  children = null,
  onPress = noop,
}) => {
  const [hover, setHover] = useState<boolean>(false);
  const [pressIn, setPressIn] = useState<boolean>(false);
  const [isDropDownVisible, setIsDropDownVisible] = useState<boolean>(false);

  const onHoverIn = () => setHover(true);
  const onHoverOut = () => setHover(false);
  const onPressIn = () => setPressIn(true);
  const onPressOut = () => setPressIn(false);

  const containerRef = useRef<any>(null);

  const onClose = () => {
    setIsDropDownVisible(false);
  };

  useOnclickOutside(containerRef, onClose);

  const color = ifElse(
    disabled,
    Colors.grey300,
    pressIn ? Colors.blue700 : Colors.blue700
  );

  const onLocalPress = () => {
    children && setIsDropDownVisible(!isDropDownVisible);
    onPress && onPress();
  };

  const wrappedChildren = React.Children.map(children, (child) =>
    React.cloneElement(child, { onCancel: onClose })
  );

  return (
    <View ref={(view) => (containerRef.current = view)}>
      <Pressable
        style={[
          styles.container,
          {
            height: size,
            width: size,
            padding: isContainer ? 0 : 0,
            borderRadius: isContainer ? size / 2 : Metrics.borderRadiusStandard,
          },
          disabled ? {} : containerStyle,
          hover && !disabled && styles.hover,
          pressIn && !disabled && styles.pressIn,
        ]}
        onPress={onLocalPress}
        disabled={disabled}
        onMouseEnter={onHoverIn}
        onMouseLeave={onHoverOut}
        onPressIn={onPressIn}
        onPressOut={onPressOut}
      >
        <SIcon type={type} size={size} color={color} />
      </Pressable>
      {isDropDownVisible ? wrappedChildren : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: Colors.transparent,
  },
  pressIn: {
    backgroundColor: Colors.midnight10,
  },
  hover: {
    backgroundColor: Colors.midnight05,
  },
});

export default SIconButton;
